import React, { useEffect, useState } from "react";
import styles from "./CaseLogCalendar.module.scss";
import PageTitle from "../../../components/pageTitle/PageTitle";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
} from "date-fns";
import DropDownButton from "../../../components/dropDownButton/DropDownButton";
import { useCreateLogReport } from "../../../hooks/useCreateLogReport";
import { ReactComponent as ArrowLeftIcon } from "./ArrowLeft.svg";
import { ReactComponent as ArrowRightIcon } from "./ArrowRight.svg";
import axiosClient from "../../../api/axiosClient";
import { useToasts } from "../../../contexts/ToastContext";
import CaseLogCard from "../../../components/caseLogCard/CaseLogCard";
import {ReactComponent as XIcon} from './X.svg';

const CaseLogCalendar = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [caseLogs, setCaseLogs] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedCaseLog, setSelectedCaseLog] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, isLastRow: false });
  const [selectedDay, setSelectedDay] = useState(null); // For 'See More' popup
  const [dayPopupPosition, setDayPopupPosition] = useState({ top: 0, left: 0 });
  const calendarWrapperRef = React.useRef(null);
  const popupRef = React.useRef(null);
  const dayPopupRef = React.useRef(null);
  const { createLogReport, isLoading: isCreatingLogReport } = useCreateLogReport();
  const { showToast } = useToasts();

  useEffect(() => {
    const fetchCaseLogsForMonth = async (date) => {
      try {
        setIsLoading(true);
        // Get the full calendar grid dates (including previous and next month dates)
        const monthStart = startOfMonth(date);
        const monthEnd = endOfMonth(date);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const startDate = format(gridStart, "yyyy-MM-dd");
        const endDate = format(gridEnd, "yyyy-MM-dd");

        const response = await axiosClient.get(
          `/case-log/calendar?startDate=${startDate}&endDate=${endDate}`,
        );

        if (response.data && Array.isArray(response.data)) {
          setCaseLogs(response.data);
        }
      } catch (error) {
        showToast(error.message, "error");
        setCaseLogs([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchCaseLogsForMonth(currentDate);
  }, [currentDate, showToast]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close single item popup
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.caseLogItem}`)
      ) {
        handleClosePopup();
      }
      // Close day (see more) popup
      if (
        dayPopupRef.current &&
        !dayPopupRef.current.contains(event.target) &&
        !event.target.closest(`.${styles.moreItems}`)
      ) {
        handleCloseDayPopup();
      }
    };

    if (selectedCaseLog || selectedDay) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [selectedCaseLog, selectedDay]);

  const getMonthDays = (date) => {
    const start = startOfWeek(startOfMonth(date), { weekStartsOn: 1 }); // Start on Monday
    const end = endOfWeek(endOfMonth(date), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const parseCaseLogDate = (dateString) => {
    // Parse DD-MM-YYYY format
    const [day, month, year] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const getCaseLogsForDate = (day) => {
    return caseLogs.filter((caseLog) => {
      const caseLogDate = parseCaseLogDate(caseLog.dateOfCase);
      return (
        caseLogDate.getDate() === day.getDate() &&
        caseLogDate.getMonth() === day.getMonth() &&
        caseLogDate.getFullYear() === day.getFullYear()
      );
    });
  };

  const getStatusClassName = (status) => {
    // Map API status values to CSS class names
    const statusMap = {
      Submitted: "status-submitted",
      "Submitted Late": "status-late",
      Incomplete: "status-incomplete",
      "Past Due": "status-late",
    };
    return statusMap[status] || "status-submitted";
  };

  const monthDays = getMonthDays(currentDate);
  const monthYear = format(currentDate, "MMMM yyyy");

  const handlePreviousMonth = () => {
    setCurrentDate(subMonths(currentDate, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(addMonths(currentDate, 1));
  };

  const handleCaseLogClick = (e, caseLog) => {
    if (!calendarWrapperRef.current) return;

    const itemRect = e.currentTarget.getBoundingClientRect();
    const containerRect = calendarWrapperRef.current.getBoundingClientRect();
    const POPUP_WIDTH = 380; // Max width of popup in SCSS
    const SPACING = 10; // Gap between item and popup

    // Calculate position relative to the container
    const itemRightInContainer = itemRect.right - containerRect.left;
    const spaceOnRight = containerRect.right - itemRect.right;
    const itemTopInContainer = itemRect.top - containerRect.top;
    const itemBottomInContainer = itemRect.bottom - containerRect.top;
    const containerHeight = containerRect.bottom - containerRect.top;

    let leftPosition;
    // If there's not enough space on right (380 + 10px gap), show on left
    if (spaceOnRight < POPUP_WIDTH + SPACING) {
      // Position on the left side of the item
      leftPosition = itemRect.left - containerRect.left - POPUP_WIDTH - SPACING;
    } else {
      // Position on the right side of the item
      leftPosition = itemRightInContainer + SPACING;
    }

    // Determine if the clicked cell is in the last 2 rows
    const monthDays = getMonthDays(currentDate);
    const calendarRows = Math.ceil(monthDays.length / 7);
    // Find the day for this caseLog
    let caseLogDay = null;
    for (let d of monthDays) {
      if (
        d.getDate() === parseInt(caseLog.dateOfCase.split('-')[0], 10) &&
        d.getMonth() + 1 === parseInt(caseLog.dateOfCase.split('-')[1], 10) &&
        d.getFullYear() === parseInt(caseLog.dateOfCase.split('-')[2], 10)
      ) {
        caseLogDay = d;
        break;
      }
    }
    const dayIndex = monthDays.findIndex(d => d.getTime() === (caseLogDay ? caseLogDay.getTime() : 0));
    const rowIndex = Math.floor(dayIndex / 7);
    const isLastTwoRows = rowIndex >= calendarRows - 2;

    let popupPos = {
      left: Math.max(0, leftPosition),
      isLastRow: isLastTwoRows,
    };

    if (isLastTwoRows) {
      // For last 2 rows: use bottom property (distance from container bottom)
      popupPos.bottom = containerHeight - itemBottomInContainer;
      popupPos.top = undefined;
    } else {
      // For other rows: use top property aligned with item top
      popupPos.top = itemTopInContainer;
      popupPos.bottom = undefined;
    }

    setPopupPosition(popupPos);
    setSelectedCaseLog(caseLog);
  };

  const handleClosePopup = () => {
    setSelectedCaseLog(null);
  };

  const handleCloseDayPopup = () => {
    setSelectedDay(null);
  };

  // Open 'See More' popup for a day
  const handleMoreClick = (e, day) => {
    if (!calendarWrapperRef.current) return;
    const itemRect = e.currentTarget.getBoundingClientRect();
    const containerRect = calendarWrapperRef.current.getBoundingClientRect();
    const POPUP_WIDTH = 440; // Match single item popup width
    const SPACING = 10;
    const itemRightInContainer = itemRect.right - containerRect.left;
    const spaceOnRight = containerRect.right - itemRect.right;
    let leftPosition;
    if (spaceOnRight < POPUP_WIDTH + SPACING) {
      leftPosition = itemRect.left - containerRect.left - POPUP_WIDTH - SPACING;
    } else {
      leftPosition = itemRightInContainer + SPACING;
    }

    // Determine if the clicked cell is in the last 3 rows
    const calendarRows = Math.ceil(monthDays.length / 7);
    const dayIndex = monthDays.findIndex(d => d.getTime() === day.getTime());
    const rowIndex = Math.floor(dayIndex / 7);
    const isLastThreeRows = rowIndex >= calendarRows - 3;

    const itemTopInContainer = itemRect.top - containerRect.top;
    const itemBottomInContainer = itemRect.bottom - containerRect.top;
    const containerHeight = containerRect.bottom - containerRect.top;

    if (isLastThreeRows) {
      setDayPopupPosition({
        left: Math.max(0, leftPosition),
        bottom: containerHeight - itemBottomInContainer,
        top: undefined,
      });
    } else {
      setDayPopupPosition({
        left: Math.max(0, leftPosition),
        top: itemTopInContainer,
        bottom: undefined,
      });
    }
    setSelectedDay(day);
  };

  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const isCurrentMonth = (day) => day.getMonth() === currentDate.getMonth();

  return (
    <>
      {(isLoading || isCreatingLogReport) && <LoadingOverlay />}
      <div className={styles.header}>
        <PageTitle label="Case Log Calendar" />
        <DropDownButton
          label="Start&nbsp;Log&nbsp;Report"
          disabled={isLoading || isCreatingLogReport}
          items={[
            {
              label: "Clinical Report",
              onClick: () => createLogReport(false),
            },
            {
              label: "Reading Day",
              onClick: () => createLogReport(true),
            },
          ]}
        />
      </div>
      <div className={styles.container}>
        <div className={styles.calendarWrapper} ref={calendarWrapperRef}>
          <div className={styles.calendarHeader}>
            <button
              className={styles.navButton}
              onClick={handlePreviousMonth}
              aria-label="Previous month"
            >
              <ArrowLeftIcon />
            </button>

            <div className={styles.monthYear}>
              <h2>{monthYear}</h2>
            </div>

            <button
              className={styles.navButton}
              onClick={handleNextMonth}
              aria-label="Next month"
            >
              <ArrowRightIcon />
            </button>
          </div>

          <div className={styles.calendarGrid}>
            <div className={styles.weekHeader}>
              {dayNames.map((day) => (
                <div key={day} className={styles.dayHeader}>
                  {day}
                </div>
              ))}
            </div>

            <div className={styles.monthDays}>
              {monthDays.map((day, index) => {
                const dayLogs = getCaseLogsForDate(day);
                const visibleCaseLogs = dayLogs.slice(0, 2);
                const moreCount = dayLogs.length - 2;
                const isOtherMonth = !isCurrentMonth(day);

                return (
                  <div
                    key={index}
                    className={`${styles.dayCell} ${isOtherMonth ? styles.otherMonth : ""}`}
                  >
                    <div className={styles.dayNumber}>{format(day, "d")}</div>

                    <div className={styles.caseLogsContainer}>
                      {visibleCaseLogs.map((caseLog, caseLogIndex) => (
                        <div
                          key={caseLogIndex}
                          className={`${styles.caseLogItem} ${styles[getStatusClassName(caseLog.status)]}`}
                          title={`Case Log - ${caseLog.logReport.id}-${caseLog.caseCount} - ${caseLog.status}`}
                          onClick={(e) => handleCaseLogClick(e, caseLog)}
                          style={{ cursor: 'pointer' }}
                        >
                          Case Log - {caseLog.logReport.id}-{caseLog.caseCount}
                        </div>
                      ))}

                      {moreCount > 0 && (
                        <div
                          className={styles.moreItems}
                          onClick={(e) => handleMoreClick(e, day)}
                          style={{ cursor: 'pointer' }}
                        >
                          +{moreCount} More
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedCaseLog && (
            <div 
              ref={popupRef}
              className={styles.caseLogPopup}
              style={{
                ...(popupPosition.top !== undefined && { top: `${popupPosition.top}px` }),
                ...(popupPosition.bottom !== undefined && { bottom: `${popupPosition.bottom}px` }),
                left: `${popupPosition.left}px`,
              }}
            >
              <div className={styles.close}>
                <XIcon onClick={handleClosePopup}/>
              </div>
              <div className={styles.popupContent}>
                <CaseLogCard
                  id={selectedCaseLog.id}
                  data={[
                    {
                      label: 'Case Date',
                      text: selectedCaseLog.dateOfCase
                    },
                    {
                      label: 'Start Time',
                      text: selectedCaseLog.startTime || '-'
                    },
                    {
                      label: 'End Time',
                      text: selectedCaseLog.endTime || '-'
                    },
                    {
                      label: 'Case Count',
                      text: selectedCaseLog.caseCount
                    },
                  ]}
                  cta={selectedCaseLog.status !== 'Deleted' ? {
                    label: 'View Cases Details',
                    link: `/log-reports/${selectedCaseLog.logReport.id}/case-logs/${selectedCaseLog.id}`
                  } : null}
                  status={selectedCaseLog.status}
                />
              </div>
            </div>
          )}

          {selectedDay && (
            <div
              ref={dayPopupRef}
              className={styles.caseLogPopup}
              style={{
                ...(dayPopupPosition.top !== undefined && { top: `${dayPopupPosition.top}px` }),
                ...(dayPopupPosition.bottom !== undefined && { bottom: `${dayPopupPosition.bottom}px` }),
                left: `${dayPopupPosition.left}px`,
                minWidth: 400,
                maxWidth: 440,
                overflowY: 'auto',
                maxHeight: 500,
              }}
            >
              <div className={styles.close}>
                <XIcon onClick={handleCloseDayPopup}/>
              </div>
              <div className={`${styles.popupContent} ${styles.dayPopupContent}`}>
                <div className={styles.dayPopupTitle}>
                  {format(selectedDay, 'd MMMM yyyy')}
                </div>
                {getCaseLogsForDate(selectedDay).map((caseLog) => (
                  <div key={caseLog.id} className={styles.dayPopupItem}>
                    <CaseLogCard
                      id={caseLog.id}
                      data={[
                        { label: 'Case Date', text: caseLog.dateOfCase },
                        { label: 'Start Time', text: caseLog.startTime || '-' },
                        { label: 'End Time', text: caseLog.endTime || '-' },
                        { label: 'Case Count', text: caseLog.caseCount },
                      ]}
                      cta={caseLog.status !== 'Deleted' ? {
                        label: 'View Cases Details',
                        link: `/log-reports/${caseLog.logReport.id}/case-logs/${caseLog.id}`
                      } : null}
                      status={caseLog.status}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

    </>
  );
};

export default CaseLogCalendar;
